import {
  ArrowUpIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { AgentSummary } from '@/hooks/useAIAgents';

import TipsMenu from './components/TipsMenu';
import ModeSelector from './ModeSelector';
import { useCommandMenu } from './useCommandMenu';

// Web Speech API types (not included in TypeScript's lib.dom.d.ts)
interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly length: number;
  readonly isFinal: boolean;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface ChatWelcomeProps {
  onExampleClick: (query: string) => void;
  onSendMessage: (message: string) => void;
  newMessage: string;
  setNewMessage: (message: string) => void;
  mode?: 'query' | 'agent' | 'auto';
  autoFocus?: boolean;
  canUseAgentMode?: boolean;
  showAuto?: boolean;
  onModeChange: (mode: 'query' | 'agent' | 'auto') => void;
  agents?: AgentSummary[];
  /** When true, disables all inputs and shows a warning that AI is disabled */
  disabled?: boolean;
  /** Called when the user clicks "Summon Hannibal" in auto mode */
  onLaunchHunt?: () => void;
}

const ChatWelcome: React.FC<ChatWelcomeProps> = ({
  onExampleClick,
  onSendMessage,
  newMessage,
  setNewMessage,
  mode = 'query',
  autoFocus = false,
  canUseAgentMode = false,
  showAuto = false,
  onModeChange,
  agents,
  disabled = false,
  onLaunchHunt,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showTipsMenu, setShowTipsMenu] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const commandMenuRef = useRef<HTMLDivElement>(null);

  const handleEntitySearch = (
    _type: 'asset' | 'risk' | 'webpage' | 'capability',
    _query: string
  ) => {
    // This won't be called anymore since we're just showing examples
  };

  const {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  } = useCommandMenu(
    newMessage,
    canUseAgentMode,
    onModeChange as (mode: 'query' | 'agent') => void,
    () => setNewMessage(''),
    handleEntitySearch,
    agents,
    mode === 'auto' ? 'agent' : mode,
    undefined // onHuntCommand - not needed in welcome screen
  );

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Close tips menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        showTipsMenu &&
        !target.closest('.tips-menu-container') &&
        !target.closest('.tips-menu-button')
      ) {
        setShowTipsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTipsMenu]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const win = window as unknown as {
        SpeechRecognition?: new () => SpeechRecognition;
        webkitSpeechRecognition?: new () => SpeechRecognition;
      };
      const SpeechRecognitionCtor =
        win.SpeechRecognition || win.webkitSpeechRecognition;
      if (SpeechRecognitionCtor) {
        recognitionRef.current = new SpeechRecognitionCtor();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
          const transcript = event.results[0][0].transcript;
          // Append to existing message or set new message
          const updatedMessage = newMessage
            ? newMessage + ' ' + transcript
            : transcript;
          setNewMessage(updatedMessage);
          setIsRecording(false);
        };

        recognitionRef.current.onerror = (
          event: SpeechRecognitionErrorEvent
        ) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          // Don't show alert for 'no-speech' - just silently stop
          if (event.error !== 'no-speech' && event.error !== 'aborted') {
            console.warn('Speech recognition failed:', event.error);
          }
        };

        recognitionRef.current.onend = () => {
          setIsRecording(false);
        };
      }
    }
  }, [newMessage, setNewMessage]);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome, Safari, or Edge.'
      );
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const examples = [
    {
      label: 'Exploitable Vulnerabilities',
      prompt:
        'Identify exploitable vulnerabilities across our attack surface that threat actors could abuse. Prioritize by severity and likelihood of exploitation. For each critical finding, provide the affected assets, attack vectors, and recommended remediation to prevent breach.',
    },
    {
      label: 'Trends & Changes',
      prompt:
        "Show me what's changed in the last 7 days. Highlight new assets discovered, newly exploitable CVEs, configuration drifts, or unusual security events. Compare our exposure trend to the previous week and flag any risk areas that are growing worse.",
    },
    {
      label: 'Exposure Management',
      prompt:
        'Analyze our continuous exposure management posture. Show attack paths that could lead to compromise, vulnerable assets requiring immediate attention, and recommended actions to reduce our attack surface. Focus on prevention-first strategies that minimize breach likelihood.',
    },
  ];

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed || disabled) return;

    // Auto-complete if there's exactly one matching command (mirror of
    // AIAssistant.tsx — same defensive clear; see that file for rationale).
    if (shouldAutoComplete) {
      selectCommand(filteredCommands[0].command);
      setNewMessage('');
      return;
    }

    // Check if it's a command
    if (executeCommandIfMatch(trimmed)) {
      return;
    }

    // Regular message
    onSendMessage(trimmed);
  };

  if (mode === 'auto') {
    return (
      <div className="flex h-full items-start justify-center px-8 pt-[30vh]">
        <div className="w-full max-w-4xl">
          <div className="mb-8 text-center">
            <h1 className="text-chariot-text-primary mb-6 text-4xl font-semibold">
              Hannibal
            </h1>
            <div className="relative mx-auto max-w-3xl">
              <Button
                onClick={() => onLaunchHunt?.()}
                className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded-full px-12 py-4 text-base font-semibold text-white"
              >
                Summon Hannibal
              </Button>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-center">
            <p className="text-chariot-text-secondary text-sm">
              Hannibal ad portas. Autonomous penetration testing powered by{' '}
              <span className="text-chariot-brand-primary font-medium">
                Praetorian Guard
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-start justify-center px-8 pt-[30vh]">
      <div className="w-full max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-chariot-text-primary mb-6 text-4xl font-semibold">
            Marcus Aurelius
          </h1>

          {/* Centered Input */}
          <form onSubmit={handleSubmit}>
            <div className="relative mx-auto max-w-3xl">
              <div
                onClick={() => textareaRef.current?.focus()}
                className={`bg-chariot-background-primary flex cursor-text items-center gap-3 rounded-full border px-4 py-3 transition-colors ${
                  disabled
                    ? 'border-chariot-stroke cursor-not-allowed opacity-60'
                    : mode === 'agent'
                      ? 'border-chariot-stroke-high hover:border-chariot-stroke-high'
                      : 'border-chariot-stroke hover:border-chariot-brand-primary'
                }`}
              >
                <button
                  type="button"
                  disabled={disabled}
                  onClick={e => {
                    e.stopPropagation();
                    if (!disabled) setShowTipsMenu(prev => !prev);
                  }}
                  className={`tips-menu-button text-chariot-text-secondary flex-shrink-0 transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:text-chariot-text-primary'}`}
                  title="Show search shortcuts"
                >
                  <LightBulbIcon className="size-5" />
                </button>
                {canUseAgentMode && !disabled && (
                  <ModeSelector
                    mode={mode}
                    onModeChange={onModeChange}
                    showAuto={showAuto}
                  />
                )}
                <textarea
                  ref={textareaRef}
                  value={newMessage}
                  disabled={disabled}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder={
                    disabled
                      ? 'Chat is unavailable'
                      : mode === 'agent'
                        ? 'Run a task against your attack surface'
                        : 'Ask anything about your attack surface'
                  }
                  className="text-chariot-text-primary placeholder-chariot-text-secondary flex-1 resize-none bg-transparent text-sm focus:outline-hidden"
                  rows={1}
                  style={{
                    minHeight: '24px',
                    maxHeight: '120px',
                  }}
                />
                <button
                  type="button"
                  disabled={disabled}
                  onClick={e => {
                    e.stopPropagation();
                    if (!disabled) toggleRecording();
                  }}
                  className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                    disabled
                      ? 'text-chariot-text-secondary cursor-not-allowed opacity-50'
                      : isRecording
                        ? 'bg-error hover:bg-error/90 animate-pulse text-white'
                        : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                  }`}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <MicrophoneIcon className="size-4" />
                </button>
                <button
                  type="submit"
                  disabled={!newMessage.trim() || disabled}
                  onClick={e => e.stopPropagation()}
                  className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    disabled
                      ? 'bg-chariot-text-secondary'
                      : 'bg-chariot-brand-primary hover:bg-chariot-brand-primary/90'
                  }`}
                >
                  <ArrowUpIcon className="size-4" />
                </button>
              </div>

              {/* Command/Search menu - show below input */}
              {showCommandMenu &&
                filteredCommands.length > 0 &&
                (menuType === 'search' || canUseAgentMode) && (
                  <div
                    ref={commandMenuRef}
                    className="command-menu bg-chariot-background-primary border-chariot-stroke absolute top-full right-0 left-0 z-50 mt-2 max-h-80 overflow-hidden rounded-lg border shadow-lg"
                  >
                    <div className="max-h-80 overflow-y-auto p-2">
                      {filteredCommands.map((cmd, index) => (
                        <button
                          key={`${cmd.command}-${index}`}
                          type="button"
                          onClick={() => {
                            // For entity search (@ or #), replace with JSON data if available
                            if (menuType === 'search' && cmd.data) {
                              setNewMessage(
                                replaceCurrentWord(cmd.command, cmd.data)
                              );
                              textareaRef.current?.focus();
                            }
                            // For search without data, just replace text
                            else if (menuType === 'search') {
                              setNewMessage(replaceCurrentWord(cmd.command));
                              textareaRef.current?.focus();
                            }
                            // For commands, execute action
                            else {
                              selectCommand(cmd.command);
                            }
                          }}
                          className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                        >
                          <div className="text-chariot-text-primary text-sm font-medium">
                            {cmd.command}
                          </div>
                          <div className="text-chariot-text-secondary text-xs">
                            {cmd.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {/* Tips menu popover */}
              <TipsMenu
                show={showTipsMenu}
                position="below"
                onTipClick={icon => {
                  setNewMessage(
                    newMessage +
                      (newMessage && !newMessage.endsWith(' ') ? ' ' : '') +
                      icon
                  );
                  textareaRef.current?.focus();
                  setShowTipsMenu(false);
                }}
              />
            </div>
          </form>
        </div>

        <div className="mt-2 flex items-center justify-center gap-4">
          <p className="text-chariot-text-secondary text-sm font-bold">
            Starter Hints:
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((example, index) => (
              <button
                key={index}
                onClick={() => onExampleClick(example.prompt)}
                disabled={disabled}
                className={`rounded-full border-2 border-dashed px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  disabled
                    ? 'border-chariot-stroke text-chariot-text-secondary cursor-not-allowed opacity-50'
                    : 'border-chariot-stroke hover:border-chariot-brand-primary text-chariot-text-secondary hover:text-chariot-text-primary'
                }`}
              >
                {example.label}
              </button>
            ))}
          </div>
        </div>

        {/* Warning when AI inference is disabled but user has bypass access */}
        {disabled && (
          <div className="mx-auto mt-6 flex max-w-3xl items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
            <ExclamationTriangleIcon className="size-5 shrink-0 text-amber-500" />
            <p className="text-chariot-text-secondary text-sm">
              LLM functionality disabled. Administrators can enable AI Inference
              under Settings {'->'} Organization.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWelcome;
